import { useState, useRef, useEffect } from 'react'
import { cn } from '@cocoonspace/ui/lib/utils'
import { motion, AnimatePresence, easeInOut } from 'motion/react'

type Phase = 'growing' | 'shrinking'

interface LoadingBarState {
	left: number
	right: number
	phase: Phase
}

const DURATION_MS = 600

export const LoadingBar = ({
	isLoading,
	className,
	barClassName,
	duration = DURATION_MS,
}: {
	isLoading?: boolean
	className?: string
	barClassName?: string
	duration?: number
}) => {
	const [state, setState] = useState<LoadingBarState>({
		left: 0,
		right: 0,
		phase: 'growing',
	})

	const startTimeRef = useRef<number | null>(null)
	const phaseRef = useRef<Phase>('growing')
	const rafRef = useRef<number | null>(null)

	useEffect(() => {
		const animate = (timestamp: number) => {
			if (startTimeRef.current === null) {
				startTimeRef.current = timestamp
			}

			const elapsed = timestamp - startTimeRef.current
			const progress = Math.min(elapsed / duration, 1)
			const eased = easeInOut(progress)

			if (phaseRef.current === 'growing') {
				setState({
					left: 0,
					right: eased * 100,
					phase: 'growing',
				})
			} else {
				setState({
					left: eased * 100,
					right: 100,
					phase: 'shrinking',
				})
			}

			if (progress < 1) {
				rafRef.current = requestAnimationFrame(animate)
			} else {
				phaseRef.current =
					phaseRef.current === 'growing' ? 'shrinking' : 'growing'
				startTimeRef.current = null

				if (phaseRef.current === 'growing') {
					setTimeout(() => {
						rafRef.current = requestAnimationFrame(animate)
					}, 120)
				} else {
					rafRef.current = requestAnimationFrame(animate)
				}
			}
		}

		rafRef.current = requestAnimationFrame(animate)

		return () => {
			if (rafRef.current !== null) {
				cancelAnimationFrame(rafRef.current)
			}
		}
	}, [duration])

	const barWidth = state.right - state.left

	return (
		<AnimatePresence>
			{isLoading && (
				<motion.div
					className={cn(
						'absolute inset-x-0 top-0 h-px w-full overflow-hidden bg-transparent',
						className,
					)}
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
				>
					<div
						className={cn('bg-primary absolute top-0 h-full', barClassName)}
						style={{
							left: `${state.left}%`,
							width: `${barWidth}%`,
						}}
					/>
				</motion.div>
			)}
		</AnimatePresence>
	)
}
